/**
 * PWA UI Components
 * Install Prompt, Update Banner, Offline Indicator, Network Status
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePWA } from '../context/PWAContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import {
  Download,
  RefreshCw,
  Wifi,
  WifiOff,
  X,
  Smartphone,
  Share,
  Plus,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

/**
 * PWA Install Prompt - показывается когда приложение можно установить
 */
export function PWAInstallPrompt({ className = '' }) {
  const { t } = useTranslation();
  const { canInstall, installPWA, isIOS, getIOSInstallInstructions, isInstalled } = usePWA();
  const [dismissed, setDismissed] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    const wasDismissed = localStorage.getItem('pwa-install-dismissed');
    if (wasDismissed) {
      const dismissedTime = parseInt(wasDismissed, 10);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
      }
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  const handleInstall = async () => {
    const success = await installPWA();
    if (success) {
      setDismissed(true);
    }
  };

  // Don't show if already installed or dismissed
  if (isInstalled || dismissed) return null;

  // iOS specific instructions
  if (isIOS) {
    const instructions = getIOSInstallInstructions();
    
    return (
      <div className={`fixed bottom-0 left-0 right-0 z-50 p-4 pb-safe ${className}`}>
        <Card className="bg-sky-600 border-0 text-white shadow-xl">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Smartphone className="h-5 w-5" />
                  <span className="font-semibold">{t('pwa.installTitleIOS', 'Установите TrafficForge')}</span>
                </div>
                
                {!showIOSInstructions ? (
                  <>
                    <p className="text-sm opacity-90 mb-3">
                      {t('pwa.installText', 'Добавьте на главный экран для быстрого доступа')}
                    </p>
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={() => setShowIOSInstructions(true)}
                      className="bg-white/20 hover:bg-white/30 text-white border-0"
                    >
                      <Share className="h-4 w-4 mr-2" />
                      {t('pwa.howToInstall', 'Как установить')}
                    </Button>
                  </>
                ) : (
                  <div className="space-y-2">
                    {instructions.steps.map((step, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <span className="bg-white/20 rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">
                          {idx + 1}
                        </span>
                        <span>{step}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <button 
                onClick={handleDismiss}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Standard install prompt (Chrome, Edge, etc.)
  if (!canInstall) return null;

  return (
    <div className={`fixed bottom-4 right-4 z-50 w-[calc(100%_-_2rem)] max-w-xl pb-safe ${className}`}>
      <Card className="bg-sky-600 border-0 text-white shadow-xl">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <Download className="h-6 w-6" />
              </div>
              <div>
                <p className="font-semibold">{t('pwa.installTitle', 'Установить TrafficForge')}</p>
                <p className="text-sm opacity-90">{t('pwa.quickAccess', 'Быстрый доступ с главного экрана')}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleDismiss}
                className="text-white hover:bg-white/20"
              >{t('pwa.later', 'Позже')}</Button>
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={handleInstall}
                className="bg-white text-sky-700 hover:bg-white/90"
              >
                <Plus className="h-4 w-4 mr-1" />{t('pwa.install', 'Установить')}</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * PWA Update Banner - показывается когда доступно обновление
 */
export function PWAUpdateBanner({ className = '' }) {
  const { updateAvailable, applyUpdate } = usePWA();
  const [dismissed, setDismissed] = useState(false);

  // Don't show in development mode to prevent confusion
  const isProduction = process.env.NODE_ENV === 'production';
  
  if (!isProduction || !updateAvailable || dismissed) return null;

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 ${className}`}>
      <Alert className="rounded-none border-x-0 border-t-0 bg-green-500 text-white border-green-600">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            <AlertDescription className="text-white">
              Доступна новая версия TrafficForge!
            </AlertDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setDismissed(true)}
              className="text-white hover:bg-white/20 h-7"
            >
              Позже
            </Button>
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={applyUpdate}
              className="bg-white text-green-600 hover:bg-white/90 h-7"
            >
              Обновить
            </Button>
          </div>
        </div>
      </Alert>
    </div>
  );
}

/**
 * Offline Indicator - показывает статус сети
 */
export function OfflineIndicator({ className = '' }) {
  const { isOnline } = usePWA();
  const [showOnlineMessage, setShowOnlineMessage] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    } else if (wasOffline) {
      setShowOnlineMessage(true);
      const timer = setTimeout(() => {
        setShowOnlineMessage(false);
        setWasOffline(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  if (isOnline && !showOnlineMessage) return null;

  return (
    <div className={`fixed bottom-20 left-1/2 -translate-x-1/2 z-50 ${className}`}>
      {!isOnline ? (
        <Badge 
          variant="destructive" 
          className="px-4 py-2 text-sm shadow-lg flex items-center gap-2"
        >
          <WifiOff className="h-4 w-4" />
          Нет подключения к интернету
        </Badge>
      ) : (
        <Badge 
          variant="default" 
          className="px-4 py-2 text-sm shadow-lg flex items-center gap-2 bg-green-500"
        >
          <Wifi className="h-4 w-4" />
          Подключение восстановлено
        </Badge>
      )}
    </div>
  );
}

/**
 * Network Quality Indicator - показывает качество соединения
 */
export function NetworkQualityIndicator({ showAlways = false }) {
  const { isOnline, connectionType } = usePWA();

  if (!connectionType && !showAlways) return null;

  const getQualityColor = () => {
    if (!isOnline) return 'text-red-500';
    if (!connectionType) return 'text-gray-500';
    
    switch (connectionType.effectiveType) {
      case '4g': return 'text-green-500';
      case '3g': return 'text-yellow-500';
      case '2g': return 'text-orange-500';
      case 'slow-2g': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getQualityLabel = () => {
    if (!isOnline) return 'Офлайн';
    if (!connectionType) return 'Неизвестно';
    
    switch (connectionType.effectiveType) {
      case '4g': return 'Отличное';
      case '3g': return 'Хорошее';
      case '2g': return 'Слабое';
      case 'slow-2g': return 'Очень слабое';
      default: return connectionType.effectiveType;
    }
  };

  return (
    <div className={`flex items-center gap-1.5 text-sm ${getQualityColor()}`}>
      {isOnline ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
      <span>{getQualityLabel()}</span>
      {connectionType?.saveData && (
        <Badge variant="outline" className="text-xs py-0 px-1">
          Экономия
        </Badge>
      )}
    </div>
  );
}

/**
 * PWA Status Badge - компактный индикатор статуса PWA
 */
export function PWAStatusBadge() {
  const { isInstalled, isStandalone } = usePWA();

  if (!isInstalled && !isStandalone) return null;

  return (
    <Badge variant="secondary" className="text-xs">
      <CheckCircle className="h-3 w-3 mr-1" />
      PWA
    </Badge>
  );
}

export default {
  PWAInstallPrompt,
  PWAUpdateBanner,
  OfflineIndicator,
  NetworkQualityIndicator,
  PWAStatusBadge
};
